import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { View, FlatList, StyleSheet, SafeAreaView } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { HeaderBackButton } from '@react-navigation/elements';
import * as airActions from '../store/actions/air';
import {
  selectOrderByAir,
  selectFilterByAir,
  selectConvertedAirMatrixData,
  selectSelectedAirPreviousResponseId,
  selectFilterByAirBrand,
  selectAirResponseSummary,
  selectAirBrands,
  selectResultsFilteredAndOrdered,
} from '../store/selectors/air.selector';
import {
  selectSearchCriteriaByComponentIndex,
  selectFirstSearchCriteria,
  selectSearchInput,
  selectChangeRes,
} from '../store/selectors/search.selector';
import { Modals, Screens } from '../constants/ScreenNames';

import { AirCard } from '../components/resultCards';
import TabBar from '../components/SegmentedControl/TabBar';

// import FloatButton from '../components/FloatButton';
import SearchFabMenuModal from '../components/SearchFabMenuModal';
import BaseText from '../components/BaseText';

import NavigateFilter from '../components/NavigateFilter';

import { airBrandMap } from '../constants/BrandMaps';
import { Color, CommonProperties, CommonStyles } from '../styles';
import useBoolean from '../utils/useBoolean';
import { isCloseToBottom } from '../utils/screen.utils';
import { isBXservice } from '../utils/search.utils';

import { movePrevious } from '../store/actions/workflow';
import HeaderTitle from '../components/shared/HeaderTitle';
import { isAirResultDisabled } from '../utils/air.util';
import { rightFilterHeaderOption } from '../utils/defaultNavOptions';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import Interstitial from '../components/modals/Interstitial';
import useTextInterstitialOptions from '../utils/useTextInterstitialOptions';
import {
  searchTypesEnum,
  AAServiceTypes,
  searchSubTypeToAirRequestTypeMap,
  AirLegs,
} from '../constants/Enums';
import PointMarker from '../components/maps/PointMarker';
import InfoMarker, { AirMarkerContent } from '../components/maps/InfoMarker';
import useAvailableHeight from '../utils/useAvailableHeight';
import useTextOrderByAirList from '../utils/useTextOrderByAirList';
import CircleSegmentedControl from '../components/SegmentedControl/CircleSegmentedControl';
import { useDateFormat } from '../utils/useDateFormat';
import {
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  selectTokenData,
} from '../store/selectors/auth.selector';
import { AA_ENABLED, EVENTS_ENABLED } from '../config/env';
import Messages from '../components/event/Messages';
import { selectTezEvent, selectTezEventGroup } from '../store/selectors/events.selector';
import { pageKeys } from '../constants/PageKeys';
import { getPoints } from '../utils/currency.utils';

export default function FlightScreen({ navigation, route }) {
  const { t } = useTranslation(i18nNS.air);
  const { tKey } = useComponentTKey('FlightScreen');
  const routeParams = route.params;
  const dispatch = useDispatch();
  const airResponsesFiltered = useSelector(selectResultsFilteredAndOrdered);
  const orderBy = useSelector(selectOrderByAir);
  const filterBy = useSelector(selectFilterByAir);
  const filterBrand = useSelector(selectFilterByAirBrand);
  const airBrands = useSelector(selectAirBrands);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);
  const airMapResults = useSelector(selectAirResponseSummary);
  const firstAirResponseKey = useSelector(selectSelectedAirPreviousResponseId);
  const matrixData = useSelector(selectConvertedAirMatrixData);
  const searchCriteria = useSelector(selectFirstSearchCriteria);
  const changeResTripInfo = useSelector(selectChangeRes);
  const selectedAirline = changeResTripInfo?.AirResponseDetails?.AirLegs[0]?.AirlineCode;
  const searchCriteriaMulticity = useSelector(selectSearchCriteriaByComponentIndex);
  const searchInput = useSelector(selectSearchInput);
  const { serviceType, authNumber } = searchInput;
  const airBrandFilters = airBrands?.map((item) => {
    const { key, awardName } = item;
    return {
      ...item,
      name: isBXservice(serviceType) ? awardName : t(tKey(key)),
      desc: t(tKey(`${key}Desc`)),
    };
  });
  const [flightLoading, setFlightLoading] = useState(false);
  const [modalVisible, showModal, hideModal] = useBoolean(false);
  const [visibleNavigateFilter, showNavigateFilter, hideNavigateFilter] = useBoolean(false);
  const [visibleMenuButton, showMenuButton, hideMenuButton] = useBoolean(true);
  const interstitialOptions = useTextInterstitialOptions(searchTypesEnum.AIR);
  const flatListRef = useRef();
  const [orderByAirList] = useTextOrderByAirList(serviceType);
  const formatDate = useDateFormat();
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const { multicity } = searchSubTypeToAirRequestTypeMap;
  const { FIRST_LEG } = AirLegs;
  const onSearchFabMenuModalPress = (value) => {
    switch (value) {
      case 0:
        navigation.navigate(Modals.AirFilterModal, { legNumber: route?.params?.legNumber });
        break;
    }
    hideModal();
  };

  const setSelectedFilter = useCallback(
    (value) => {
      dispatch(airActions.filterByBrand(value));
    },
    [dispatch]
  );

  const onChangeSegmented = useCallback(
    (value) => {
      dispatch(airActions.orderBy(value));
    },
    [dispatch]
  );

  const onChangeBarSegmented = useCallback(
    (filterBy) => {
      dispatch(airActions.filterBy(filterBy));
    },
    [dispatch]
  );

  useLayoutEffect(() => {
    const fetchBXFlights = async () => {
      setFlightLoading(true);
      await dispatch(airActions.airBXSearch({ ...route.params, authNumber: '' }));
      setFlightLoading(false);
    };

    const getTitle = () => {
      const filterBrandObject = airBrandFilters?.find((filter) => filter.brand === filterBrand);
      if (flightLoading) {
        return t(tKey('Loading'));
      }
      if (airBrandFilters) {
        return isBXservice(serviceType)
          ? `${filterBrandObject?.name} - ${getPoints(
              filterBrandObject?.price
            )} ${filterBrandObject?.brand?.capitalize()}`
          : t(tKey(airBrandMap[filterBrand]?.name));
      }
      return t(tKey('NoResults'));
    };

    const getSubtitle = () => {
      if (searchInput.airRequestType !== multicity) {
        return routeParams.legNumber === FIRST_LEG
          ? t(tKey('Depart')) + formatDate(searchCriteria?.fromDate)
          : formatDate(searchCriteria?.toDate);
      }
      return t(tKey('Depart')) + formatDate(searchCriteriaMulticity?.fromDate);
    };

    navigation.setOptions({
      headerLeft: (headerLeftProps) => (
        <HeaderBackButton
          {...headerLeftProps}
          onPress={() => {
            if (AA_ENABLED && route.params.legNumber === FIRST_LEG) {
              navigation.navigate(Screens.AASearchScreen);
            } else {
              dispatch(movePrevious());
            }
          }}
        />
      ),
      headerTitle: () => {
        return (
          <HeaderTitle
            title={getTitle()}
            subtitle={getSubtitle()}
            onPress={airBrandFilters && showNavigateFilter}
          />
        );
      },
      ...rightFilterHeaderOption(
        () =>
          airBrandFilters &&
          navigation.navigate(Modals.AirFilterModal, { legNumber: route?.params?.legNumber })
      ),
      headerTitleAlign: 'center',
    });
    if (route.params.fetchBX) {
      fetchBXFlights();
      route.params.fetchBX = false;
    }
  }, [
    navigation,
    routeParams.legNumber,
    filterBrand,
    showNavigateFilter,
    searchCriteria,
    dispatch,
    flightLoading,
    airBrandFilters,
    t,
    tKey,
    searchInput,
    searchCriteriaMulticity,
    formatDate,
    route.params,
    FIRST_LEG,
    multicity,
    serviceType,
  ]);

  useEffect(() => {
    async function fetchFlights() {
      setFlightLoading(true);
      await dispatch(
        airActions.airSearch(
          route.params,
          serviceType,
          firstAirResponseKey,
          selectedAirline,
          changeResTripInfo
        )
      );
      setFlightLoading(false);
    }
    if (isBXservice(serviceType) && route.params.legNumber === FIRST_LEG) {
      (async () => {
        setFlightLoading(true);
        const isSkipCalendar = await dispatch(airActions.getIsSkipCalendar(''));
        setFlightLoading(false);
        if (isSkipCalendar) {
          //action to display flights without showing calendar
        } else {
          navigation.navigate(Modals.AACalendarModal, {
            returnScreen: route.name,
            authNumber: '',
            allowRange: false,
            fromDate: searchInput.searchCriteria[0].fromDate,
            toDate: searchInput.searchCriteria[0].toDate,
            airRequestType: searchInput.airRequestType,
          });
        }
      })();
    } else {
      //normal flow
      fetchFlights();
    }
  }, [changeResTripInfo, dispatch, route.params.legNumber, selectedAirline, AAServiceTypes]); //todo write useMountEffect to show intent on run once on mount

  useEffect(() => {
    if (airResponsesFiltered && flatListRef.current) {
      flatListRef.current.scrollToOffset({ animated: true, offset: 0 });
    }

    showMenuButton();
  }, [airResponsesFiltered, showMenuButton]);

  return (
    <SafeAreaView style={styles.container}>
      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.AIR_LIST}
      />
      <View style={styles.wrapper}>
        <View style={CommonStyles.flex1}>
          {flightLoading ? (
            <Interstitial icon="flight" options={interstitialOptions} />
          ) : (
            <>
              <CircleSegmentedControl
                items={orderByAirList}
                onChange={onChangeSegmented}
                selected={orderBy}
              />
              <FlatList
                ref={flatListRef}
                contentContainerStyle={styles.contentContainerStyle}
                data={airResponsesFiltered}
                keyExtractor={(item) => item.ResponseID}
                renderItem={({ item }) => (
                  <AirCard
                    showBrands
                    showPrice
                    disabled={isAirResultDisabled(item)}
                    style={CommonStyles.boxShadow}
                    flight={item}
                    currency={currency}
                    currencyCulture={currencyCulture}
                    legNumber={route.params.legNumber}
                    setFlightLoading={setFlightLoading}
                    onPress={() => {
                      dispatch(
                        airActions.selectAir(
                          item,
                          item.MultiBrands[item.selectedBrand],
                          route.params.legNumber
                        )
                      );
                    }}
                  />
                )}
                ListEmptyComponent={() => (
                  <BaseText style={styles.noResultsText}>{t(tKey('NoFlights'))}</BaseText>
                )}
                onScroll={({ nativeEvent }) => {
                  if (isCloseToBottom(nativeEvent)) {
                    hideMenuButton();
                  } else if (!visibleMenuButton) {
                    showMenuButton();
                  }
                }}
              />
            </>
          )}
        </View>
        <TabBar
          onChange={onChangeBarSegmented}
          selected={filterBy}
          loading={flightLoading}
          items={
            flightLoading
              ? [
                  { second: t(tKey('ALL')) },
                  { second: t(tKey('MORNING')) },
                  { second: t(tKey('MIDDAY')) },
                  { second: t(tKey('EVENING')) },
                ]
              : matrixData?.map((i) => {
                  i.second = t(tKey(i.value));
                  return i;
                })
          }
        />
        {/* {visibleMenuButton && <FloatButton onPress={showModal} iconName="menu" />} */}
        <SearchFabMenuModal
          title={t(tKey('FilterFlights'))}
          onPress={onSearchFabMenuModalPress}
          onClose={hideModal}
          visible={modalVisible}
        />
        <NavigateFilter
          onRequestClose={hideNavigateFilter}
          onPress={setSelectedFilter}
          selected={filterBrand}
          items={airBrandFilters}
          visible={visibleNavigateFilter}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  mapStyle: {
    ...StyleSheet.absoluteFillObject,
  },
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  wrapper: {
    height: '100%',
    justifyContent: 'space-between',
  },
  contentContainerStyle: {
    flexGrow: 1,
  },
  noResultsText: {
    textAlign: 'center',
    ...CommonProperties.mt3,
  },
});
